/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  GamoraControllerContextIdentifier,
  GamoraFoundationGlueListenerType,
  GamoraFoundationNodeArgs,
  assert,
  debugCreate,
} from '@ver.id/libs.internal.gamora';
import {
  HoruzProtocolChulakMessageAppPairingCompletedGuard,
  HoruzProtocolChulakMessageAppPairingExitGuard,
} from '../../guards/message/index.js';
import {
  HoruzProtocolChulakServiceStateInvoker,
  HoruzProtocolChulakServiceStateInvokerCompletedAppPairingInput,
  HoruzProtocolChulakServiceStateInvokerCreateAppPairingInput,
  HoruzProtocolChulakServiceStateInvokerCreateAppPairingResult,
} from './enums.js';
import { HoruzProtocolChulakDataType } from '../../data.js';
import { HoruzProtocolChulakErrorProtocolInvalidMessage } from '../../errors/protocol/invalid-message.error.js';
import { HoruzProtocolChulakLastState } from './../../states.js';
import { HoruzProtocolChulakMessageAppPairingEnterGuard } from '../../guards/message/app-pairing-enter.js';
import { HoruzProtocolChulakMessageModule } from '../../modules/message.js';
import { HoruzProtocolChulakServiceStateBase } from './base.js';
import { HoruzProtocolChulakServiceStateContext } from '../context.js';
import { HoruzProtocolChulakServiceStateMap } from '../../statemap.js';
import { IHoruz } from '../../../../horuz.js';
const debug = debugCreate({
  namespace: 'horuz:protocols:chulak:states:server:app-pairing',
});

/**
 * Server
 *
 * AppPairing state
 * @public
 */
export class HoruzProtocolChulakServiceStateAppPairing extends HoruzProtocolChulakServiceStateBase<
  typeof HoruzProtocolChulakServiceStateMap,
  HoruzProtocolChulakServiceStateContext,
  IHoruz
> {
  constructor(args: GamoraFoundationNodeArgs) {
    super(args);
  }

  /**
   * Event on message
   * @param contextIdentifier The context identifier
   */
  private _onQueueNext = (
    serviceStateContext: HoruzProtocolChulakServiceStateContext,
  ) => {
    return this.$tryPostTask(serviceStateContext, async () => {
      try {
        // Execute state
        const result = await this.recv(serviceStateContext);
        if (result.done == false) {
          // Decode message type
          switch (result.message.type) {
            case HoruzProtocolChulakDataType.DATA_APP_PAIRING_COMPLETED: {
              await this._onAppPairingCompletedMessage(
                serviceStateContext,
                result.message,
              );
              break;
            }
            default: {
              throw HoruzProtocolChulakErrorProtocolInvalidMessage.Create(
                'Message ' +
                  result.message.type +
                  ' not supported in AppPairing state.',
              );
            }
          }
        }
      } catch (e: any) {
        debug.log('($enter): Unable to handle serviceSession. ');
        debug.trace(e?.message, e?.stack);
        this._tryHandleException(serviceStateContext, e);
      }
    });
  };

  /**
   * Event
   *
   * Handle
   */
  private async _onAppPairingCompletedMessage(
    serviceStateContext: HoruzProtocolChulakServiceStateContext,
    message: HoruzProtocolChulakMessageAppPairingCompletedGuard,
  ) {
    assert(
      serviceStateContext.stateContextRegistry.serviceSessionUuid,
      'Invalid argument: serviceSessionUuid not available in stateContextRegistry.',
    );

    // Update service session state (inside lock for atomicity)
    await this.serviceSessionStore.withLock(
      serviceStateContext.stateContextRegistry.serviceSessionUuid,
      async () => {
        assert(
          serviceStateContext.stateContextRegistry.serviceSessionUuid,
          'Invalid argument: serviceSessionUuid not available in stateContextRegistry.',
        );

        // Update service session
        const hasServiceSession = await this.serviceSessionStore.has(
          serviceStateContext.stateContextRegistry.serviceSessionUuid,
        );
        if (hasServiceSession) {
          // Get service session
          const serviceSession = await this.serviceSessionStore.get(
            serviceStateContext.stateContextRegistry.serviceSessionUuid,
          );

          // Check if message exists
          if (
            !serviceSession.hasDataByType(
              HoruzProtocolChulakDataType.DATA_APP_PAIRING_COMPLETED,
            )
          ) {
            const appPairingCompleted =
              HoruzProtocolChulakMessageAppPairingCompletedGuard.fromJSON({});

            // Set data to service session
            serviceSession.setData(
              HoruzProtocolChulakDataType.DATA_APP_PAIRING_COMPLETED,
              HoruzProtocolChulakMessageModule.Encode(appPairingCompleted),
            );

            // Store service session
            await this.serviceSessionStore.upsert(serviceSession);
          }
        }
      },
    );

    // Notify pairing completed (outside lock - user code can acquire locks freely)
    await this.$fireInvocation(
      serviceStateContext.contextIdentifier,
      HoruzProtocolChulakServiceStateInvoker.CompletedAppPairing,
      serviceStateContext,
      {
        serviceSessionUuid:
          serviceStateContext.stateContextRegistry.serviceSessionUuid,
      } as HoruzProtocolChulakServiceStateInvokerCompletedAppPairingInput,
    );

    // Pairing completed
    debug.log('(_onAppPairingCompletedMessage): Pairing completed.');

    // Switch state
    this.$tryPostTransition(serviceStateContext, {
      stateNext: 'AppPresentation',
    });
  }

  /**
   * Enter state
   */
  override async $enter(
    serviceStateContext: HoruzProtocolChulakServiceStateContext,
  ): Promise<void> {
    assert(
      serviceStateContext.stateContextRegistry.serviceSessionUuid,
      'Invalid argument: serviceSessionUuid not available in stateContextRegistry.',
    );
    try {
      // Listen for messages
      this.$root
        .$('queues')
        .$('data')
        .$on(serviceStateContext.contextIdentifier, 'next', () =>
          this._onQueueNext(serviceStateContext),
        );

      // Execute invocation outside lock (user code can acquire locks freely)
      const pairingResult =
        await this.$executeInvocation<HoruzProtocolChulakServiceStateInvokerCreateAppPairingResult>(
          serviceStateContext.contextIdentifier,
          HoruzProtocolChulakServiceStateInvoker.CreateAppPairing,
          serviceStateContext,
          {
            serviceSessionUuid:
              serviceStateContext.stateContextRegistry.serviceSessionUuid,
          },
        );

      // Get pairing message
      const pairingEnter =
        HoruzProtocolChulakMessageAppPairingEnterGuard.fromJSON(pairingResult);

      // Process session (inside lock for atomicity)
      return await this.serviceSessionStore.withLock(
        serviceStateContext.stateContextRegistry.serviceSessionUuid,
        async () => {
          try {
            assert(
              serviceStateContext.stateContextRegistry.serviceSessionUuid,
              'Invalid argument: serviceSessionUuid not available in stateContextRegistry.',
            );

            // Get session
            const serviceSession = await this.serviceSessionStore.get(
              serviceStateContext.stateContextRegistry.serviceSessionUuid,
            );

            // Send start message
            await this.send(serviceStateContext, pairingEnter);

            // Store
            serviceSession.lastState = HoruzProtocolChulakLastState.AppPairing;
            await this.serviceSessionStore.upsert(serviceSession);

            // Check session (recovery scenario)
            if (
              serviceSession.hasDataByType(
                HoruzProtocolChulakDataType.DATA_APP_PAIRING_COMPLETED,
              )
            ) {
              // Post transition
              this.$tryPostTransition(serviceStateContext, {
                stateNext: 'AppPresentation',
              });

              // Return
              return;
            }
          } catch (e: any) {
            debug.log('($enter): Unable to handle serviceSession. ');
            debug.trace(e?.message, e?.stack);
            this._tryHandleException(serviceStateContext, e);
          }
        },
      );
    } catch (e: any) {
      debug.log('($enter): Unable to get serviceSession lock. ');
      debug.trace(e?.message, e?.stack);
      this._tryHandleException(serviceStateContext, e);
    }
  }

  /**
   * Exit state
   */
  override async $exit(
    serviceStateContext: HoruzProtocolChulakServiceStateContext,
  ): Promise<void> {
    assert(
      serviceStateContext.stateContextRegistry.serviceSessionUuid,
      'Invalid argument: serviceSessionUuid not available in stateContextRegistry.',
    );
    try {
      // Do not listen to messages anymore
      this.$root
        .$('queues')
        .$('data')
        .$removeEventListenersByNamespaceAndName(
          serviceStateContext.contextIdentifier,
          'next',
        );

      // Aborting or stopping are the next states
      const contextState = this.$controller.$getContext(
        serviceStateContext.contextIdentifier,
      );
      if (
        contextState.stateNext == 'Abort' ||
        contextState.stateNext == 'Stopped'
      ) {
        return;
      }

      // Finalize
      return await this.serviceSessionStore.withLock(
        serviceStateContext.stateContextRegistry.serviceSessionUuid,
        async () => {
          try {
            assert(
              serviceStateContext.stateContextRegistry.serviceSessionUuid,
              'Invalid argument: serviceSessionUuid not available in stateContextRegistry.',
            );

            // Get session
            const serviceSession = await this.serviceSessionStore.get(
              serviceStateContext.stateContextRegistry.serviceSessionUuid,
            );

            // Get pairing message
            const pairingExit =
              HoruzProtocolChulakMessageAppPairingExitGuard.fromJSON({});

            // Send pairing code
            await this.send(serviceStateContext, pairingExit);

            // Store
            await this.serviceSessionStore.upsert(serviceSession);
          } catch (e: any) {
            debug.log('($exit): Unable to handle serviceSession. ');
            debug.trace(e?.message, e?.stack);
            this._tryHandleException(serviceStateContext, e);
          }
        },
      );
    } catch (e: any) {
      debug.log('($exit): Unable to get serviceSession lock. ');
      debug.trace(e?.message, e?.stack);
      this._tryHandleException(serviceStateContext, e);
    }
  }

  /**
   * Register invokers
   */
  invoker(
    contextIdentifier: GamoraControllerContextIdentifier,
    name: typeof HoruzProtocolChulakServiceStateInvoker.CreateAppPairing,
    listener: (
      stateContext: HoruzProtocolChulakServiceStateContext,
      input: HoruzProtocolChulakServiceStateInvokerCreateAppPairingInput,
    ) => Promise<HoruzProtocolChulakServiceStateInvokerCreateAppPairingResult>,
  ): this;
  invoker(
    contextIdentifier: GamoraControllerContextIdentifier,
    name: typeof HoruzProtocolChulakServiceStateInvoker.CompletedAppPairing,
    listener: (
      stateContext: HoruzProtocolChulakServiceStateContext,
      input: HoruzProtocolChulakServiceStateInvokerCompletedAppPairingInput,
    ) => Promise<void>,
  ): this;
  invoker<K extends keyof typeof HoruzProtocolChulakServiceStateInvoker>(
    contextIdentifier: GamoraControllerContextIdentifier,
    name: K,
    listener: GamoraFoundationGlueListenerType,
  ): this {
    return super.$registerInvoker(contextIdentifier, name, listener);
  }
}
